import { create } from 'zustand';
import type { Model } from '../types';
import { modelsService } from '../services/modelsService';

export interface ModelGroup {
  provider: string;
  models: Model[];
}

interface ModelsState {
  catalog: Model[]; // modelos de chat (rótulos, admin)
  embeddings: Model[]; // modelos de embedding (RAG)
  allowed: Model[]; // chat efetivos do usuário logado (Pessoa ∩ Área)
  loaded: boolean;
  loading: boolean;
  load: () => Promise<void>;
  reloadAllowed: () => Promise<void>;
  labelOf: (id: string) => string;
  isAllowed: (id: string) => boolean;
}

// Helper PURO (use com useMemo no componente — NÃO chame dentro de um seletor zustand,
// senão cria um array novo a cada render e dispara loop infinito).
export function groupModelsByProvider(models: Model[]): ModelGroup[] {
  const map = new Map<string, Model[]>();
  for (const m of models) {
    if (!map.has(m.provider)) map.set(m.provider, []);
    map.get(m.provider)!.push(m);
  }
  return Array.from(map.entries()).map(([provider, models]) => ({ provider, models }));
}

export const useModelsStore = create<ModelsState>((set, get) => ({
  catalog: [],
  embeddings: [],
  allowed: [],
  loaded: false,
  loading: false,

  load: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const [catalog, embeddings, allowed] = await Promise.all([
        modelsService.list(),
        modelsService.listEmbeddings(),
        modelsService.listAllowed(),
      ]);
      set({ catalog, embeddings, allowed, loaded: true });
    } finally {
      set({ loading: false });
    }
  },

  reloadAllowed: async () => {
    const allowed = await modelsService.listAllowed();
    set({ allowed });
  },

  labelOf: (id) => {
    const m = get().catalog.find((x) => x.id === id) || get().embeddings.find((x) => x.id === id);
    return m ? m.label : id;
  },

  isAllowed: (id) => get().allowed.some((m) => m.id === id),
}));
